import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  SubscriptionHookOptions,
  SubscriptionResult,
} from '@apollo/client';
import { Maybe } from '@kinetic-ui/data-layer';
import { useTheme } from '@mui/material';
import { LoadingMessages, useGraphLoading } from 'kineticUtils';
import { throttle } from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

export interface ConnectionNode {
  node: any;
}
export interface ConnectionResult {
  edges: ConnectionNode[];
  pageInfo: {
    startCursor?: string | null;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    endCursor?: string | null;
  };
  totalCount?: number | null;
}

export interface PagedConnectionVariables {
  after?: Maybe<string>;
}

export type ConnectionQueryResult<QueryKey extends string, TResult extends ConnectionResult> = {
  [key in QueryKey]: TResult;
};

export type GenericSubscriptionResult<TData, TVariables> = SubscriptionResult<TData, TVariables>;

/**
 * Helper hook to query the function result and return the merged results of all the data.
 *
 * @param queryFunction to call when paging.
 * @param rootNode for selecting the root node out of the given query result.
 * @param options for modifying the query, ie variables,
 * @param subscriptions a boolean flag indicating whether to use subscriptions or not.
 * @param subscriptionFunction to call when there is a subscription.
 */
export const useInfiniteLoaderQuery = <
  QueryKey extends string,
  GenericConnectionQueryResult extends ConnectionQueryResult<QueryKey, ConnectionResult>,
  TVariables extends PagedConnectionVariables,
  TSubscriptionData extends { [key: string]: any },
  TSubscriptionVariables extends OperationVariables
>(
  queryFunction:
    | ((
        options?: QueryHookOptions<GenericConnectionQueryResult, TVariables>
      ) => QueryResult<GenericConnectionQueryResult, TVariables>)
    | ((
        options: QueryHookOptions<GenericConnectionQueryResult, TVariables>
      ) => QueryResult<GenericConnectionQueryResult, TVariables>),
  rootNode: QueryKey,
  loadingKey: string,
  variables?: TVariables,
  subscriptions?: boolean,
  subscriptionFunction?: (
    options?: SubscriptionHookOptions<TSubscriptionData, TSubscriptionVariables>
  ) => GenericSubscriptionResult<TSubscriptionData, TSubscriptionVariables>,
  messages?: LoadingMessages,
  hideTotalCount?: boolean,
  onSubscription?: () => void,
  subscriptionVariables?: TSubscriptionVariables
) => {
  const { t } = useTranslation('common');
  const { palette } = useTheme();
  const result = queryFunction({
    variables,
    notifyOnNetworkStatusChange: true,
  });
  const [, doneLoading] = useGraphLoading({ result, name: loadingKey, messages });
  const { data, loading, fetchMore, error } = result;
  const refreshInfinite = (params?: TVariables) => {
    if (params) {
      result.refetch(params);
    } else {
      result.refetch();
    }
  };

  const throttledRefetch = useRef(throttle(result.refetch, 1000));

  subscriptionFunction?.({
    fetchPolicy: 'network-only',
    onSubscriptionData: () => {
      if (!data?.[rootNode].pageInfo.hasPreviousPage) {
        throttledRefetch.current();
      }
      onSubscription?.();
    },
    skip: !subscriptions,
    variables: subscriptionVariables,
  });

  const InViewComponent = !!data ? (
    <tbody>
      <tr style={{ pointerEvents: 'none' }}>
        <td colSpan={100}>
          <InView
            rootMargin='30px 0px 0px 0px'
            className='inView'
            style={{ height: 20, width: '100%' }}
            onChange={(inView) => {
              if (!data[rootNode]) {
                console.error(
                  `Could not find infinite loader result for ${rootNode} the return has keys: [${Object.keys(
                    data
                  ).join(', ')}]. Please ensure the rootNode is set correctly for this query.`
                );
                return;
              }
              const { hasNextPage, endCursor } = data[rootNode].pageInfo;
              if (!inView || !hasNextPage || loading) {
                return;
              }
              fetchMore({
                variables: {
                  after: endCursor,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  const edges = fetchMoreResult[rootNode].edges;
                  return {
                    [rootNode]: {
                      totalCount: fetchMoreResult[rootNode].totalCount,
                      pageInfo: fetchMoreResult[rootNode].pageInfo,
                      edges: [...previousResult[rootNode].edges, ...edges],
                    },
                    // force the type here since typescript can't quite completely ensure the typings otherwise.
                  } as GenericConnectionQueryResult;
                },
              }).catch(() => {
                doneLoading('error');
              });
            }}
          />
        </td>
      </tr>
    </tbody>
  ) : null;

  const infiniteFooter = (
    <div
      style={{
        display: 'flex',
        width: '100%',
        padding: 7,
        flexDirection: 'column',
        alignItems: 'flex-end',
        borderTop: `1px solid ${palette.divider}`,
      }}
    >
      <div>
        {(() => {
          const edgesLength = data?.[rootNode]?.edges?.length ?? 0;
          const totalCount = data?.[rootNode]?.totalCount;
          const hasNextPage = data?.[rootNode]?.pageInfo?.hasNextPage;

          let totalLabel: string | number;
          if (!hideTotalCount) {
            totalLabel = totalCount || (hasNextPage ? t('many') : edgesLength);
          } else {
            totalLabel = hasNextPage ? t('many') : edgesLength;
          }

          return `${edgesLength} of ${totalLabel}`;
        })()}
      </div>
    </div>
  );

  return {
    infiniteFooter,
    throttledRefetch,
    refetch: throttledRefetch,
    refreshInfinite,
    data,
    error,
    loading,
    InViewComponent,
  };
};
